import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';
import axiosInstance from '../interceptors/axios';

const Navbar = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isLoggedIn, setIsLoggedIn, email } = props;
    const [schedulingEnabled, setSchedulingEnabled] = useState(true);
    const [togglingScheduling, setTogglingScheduling] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [regyboxName, setRegyboxName] = useState('');
    const [telegramBotToken, setTelegramBotToken] = useState('');
    const [telegramChatId, setTelegramChatId] = useState('');
    const [savingSettings, setSavingSettings] = useState(false);
    const [usersOpen, setUsersOpen] = useState(false);
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [expandedUserId, setExpandedUserId] = useState(null);
    const [expandedEmailsUserId, setExpandedEmailsUserId] = useState(null);

    const isAdmin = email === 'fabio.m.pedrosa@gmail.com';

    useEffect(() => {
        if (isLoggedIn) {
            axiosInstance.get('/scheduling-enabled')
                .then(res => setSchedulingEnabled(res.data.scheduling_enabled))
                .catch(() => {});
        }
    }, [isLoggedIn]);

    const openSettings = () => {
        axiosInstance.get('/notification-settings')
            .then(res => {
                setRegyboxName(res.data.regybox_name || '');
                setTelegramBotToken(res.data.telegram_bot_token || '');
                setTelegramChatId(res.data.telegram_chat_id || '');
            })
            .catch(() => {});
        setSettingsOpen(true);
    };

    const handleSaveSettings = () => {
        setSavingSettings(true);
        axiosInstance.post('/set-notification-settings', {
            regybox_name: regyboxName,
            telegram_bot_token: telegramBotToken,
            telegram_chat_id: telegramChatId,
        })
            .then(res => {
                setRegyboxName(res.data.regybox_name || '');
                setTelegramBotToken(res.data.telegram_bot_token || '');
                setTelegramChatId(res.data.telegram_chat_id || '');
                setSettingsOpen(false);
            })
            .catch(err => {
                console.error('Error saving settings:', err);
            })
            .finally(() => setSavingSettings(false));
    };

    const handleToggleScheduling = () => {
        setTogglingScheduling(true);
        const newValue = !schedulingEnabled;
        axiosInstance.post('/set-scheduling-enabled', { enabled: newValue })
            .then(res => {
                setSchedulingEnabled(res.data.scheduling_enabled);
            })
            .catch(err => {
                console.error('Error toggling scheduling:', err);
            })
            .finally(() => setTogglingScheduling(false));
    };

    const openUsers = () => {
        setUsersOpen(true);
        setLoadingUsers(true);
        axiosInstance.get('/list-users')
            .then(res => setUsers(res.data.users || []))
            .catch(err => console.error('Error loading users:', err))
            .finally(() => setLoadingUsers(false));
    };

    const handleImpersonate = (targetEmail) => {
        if (!window.confirm(`Impersonate ${targetEmail}? You'll be logged in as them — log out and back in to return to your account.`)) {
            return;
        }
        axiosInstance.post('/impersonate', { email: targetEmail })
            .then(res => {
                Cookies.set('access_token', res.data.access_token, { expires: 60 });
                Cookies.set('refresh_token', res.data.refresh_token, { expires: 60 });
                Cookies.set('regybox_token', res.data.regybox_token, { expires: 60 });
                window.location.href = '/dashboard/view-booked-classes';
            })
            .catch(err => {
                console.error('Error impersonating:', err);
                window.alert('Failed to impersonate user.');
            });
    };

    const handleLogout = () => {
        const refreshToken = Cookies.get('refresh_token');

        if (refreshToken) {
            axiosInstance.post('/blacklist', { refresh_token: refreshToken })
                .then(response => {
                    console.log('Token blacklisted successfully:', response);
                    Cookies.remove('access_token');
                    Cookies.remove('refresh_token');
                    setIsLoggedIn(false);
                    navigate('/');
                })
                .catch(error => {
                    console.error('Error blacklisting token:', error.response ? error.response.data : error);
                });
        } else {
            console.log('No refresh token found.');
        }
    };

    const isActive = (path) => location.pathname.startsWith(path);

    return (
        <>
        <header className="rb-navbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                <span className="rb-nav-brand">RegyBox</span>
                <nav className="rb-nav-links">
                    {isLoggedIn && (
                        <>
                            <Link
                                to="/dashboard/book-class"
                                className={`rb-nav-link ${isActive('/dashboard/book-class') ? 'rb-nav-link--active' : ''}`}
                            >
                                Calendar
                            </Link>
                            <Link
                                to="/dashboard/view-booked-classes"
                                className={`rb-nav-link ${isActive('/dashboard/view-booked-classes') ? 'rb-nav-link--active' : ''}`}
                            >
                                My Classes
                            </Link>
                        </>
                    )}
                </nav>
            </div>
            <div className="rb-nav-right">
                {isLoggedIn && (
                    <>
                        <div
                            className="rb-toggle"
                            style={{ cursor: togglingScheduling ? 'wait' : 'pointer' }}
                            onClick={!togglingScheduling ? handleToggleScheduling : undefined}
                            title={schedulingEnabled ? 'Auto-booking is ON — click to disable' : 'Auto-booking is OFF — click to enable'}
                        >
                            <div className={`rb-toggle-track ${schedulingEnabled ? 'rb-toggle-track--on' : 'rb-toggle-track--off'}`}>
                                <div className={`rb-toggle-thumb ${schedulingEnabled ? 'rb-toggle-thumb--on' : 'rb-toggle-thumb--off'}`} />
                            </div>
                            <span className={`rb-toggle-label ${schedulingEnabled ? 'rb-toggle-label--on' : 'rb-toggle-label--off'}`}>
                                Auto
                            </span>
                        </div>
                        <span className="rb-nav-email">{email}</span>
                        {isAdmin && (
                            <button
                                className="rb-btn-ghost"
                                onClick={openUsers}
                                title="View users"
                            >
                                Users
                            </button>
                        )}
                        <button
                            className="rb-btn-ghost"
                            onClick={openSettings}
                            title="Notification settings"
                        >
                            Settings
                        </button>
                        <button className="rb-btn-ghost" onClick={handleLogout}>
                            Logout
                        </button>
                    </>
                )}
                {!isLoggedIn && (
                    <Link to="/login" className="rb-btn rb-btn--amber">Login</Link>
                )}
            </div>
        </header>
        {settingsOpen && (
                <div className="rb-modal-overlay" onClick={() => setSettingsOpen(false)}>
                    <div className="rb-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="rb-modal-title">Notification Settings</h3>
                        <label className="rb-modal-label">
                            Regybox name
                            <input
                                className="rb-modal-input"
                                type="text"
                                value={regyboxName}
                                onChange={(e) => setRegyboxName(e.target.value)}
                                placeholder="Name as shown in Regybox class list"
                            />
                        </label>
                        <p className="rb-modal-hint">
                            Used to find your queue position. Must match your name in the Regybox enrolled list exactly.
                        </p>
                        <label className="rb-modal-label">
                            Telegram bot token
                            <input
                                className="rb-modal-input"
                                type="text"
                                value={telegramBotToken}
                                onChange={(e) => setTelegramBotToken(e.target.value)}
                                placeholder="From @BotFather, e.g. 123456:ABC-DEF…"
                            />
                        </label>
                        <label className="rb-modal-label">
                            Telegram chat ID
                            <input
                                className="rb-modal-input"
                                type="text"
                                value={telegramChatId}
                                onChange={(e) => setTelegramChatId(e.target.value)}
                                placeholder="Send /start to your bot, then check getUpdates"
                            />
                        </label>
                        <p className="rb-modal-hint">
                            Create a bot with @BotFather, send it a message, then open
                            api.telegram.org/bot&lt;token&gt;/getUpdates to find your chat ID.
                            To change the sender name shown in Telegram, use /setname in @BotFather.
                        </p>
                        <div className="rb-modal-actions">
                            <button
                                className="rb-btn-ghost"
                                onClick={() => setSettingsOpen(false)}
                                disabled={savingSettings}
                            >
                                Cancel
                            </button>
                            <button
                                className="rb-btn rb-btn--amber"
                                onClick={handleSaveSettings}
                                disabled={savingSettings}
                            >
                                {savingSettings ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {usersOpen && (
                <div className="rb-modal-overlay" onClick={() => setUsersOpen(false)}>
                    <div className="rb-modal rb-modal--wide" onClick={(e) => e.stopPropagation()}>
                        <h3 className="rb-modal-title">Users ({users.length})</h3>
                        {loadingUsers ? (
                            <p className="rb-modal-hint">Loading…</p>
                        ) : (
                            <table className="rb-users-table">
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Auto</th>
                                        <th>Regybox name</th>
                                        <th>Telegram</th>
                                        <th>Sched.</th>
                                        <th>Recur.</th>
                                        <th>Emails</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(u => (
                                        <React.Fragment key={u.id}>
                                            <tr>
                                                <td>{u.email}</td>
                                                <td>{u.scheduling_enabled ? '✅' : '—'}</td>
                                                <td>{u.regybox_name || '—'}</td>
                                                <td>{u.has_telegram ? '✅' : '—'}</td>
                                                <td>
                                                    {u.scheduled_count > 0 ? (
                                                        <button
                                                            className="rb-users-expand"
                                                            onClick={() => setExpandedUserId(
                                                                expandedUserId === u.id ? null : u.id
                                                            )}
                                                        >
                                                            {u.scheduled_count} {expandedUserId === u.id ? '▲' : '▼'}
                                                        </button>
                                                    ) : '0'}
                                                </td>
                                                <td>{u.recurring_count}</td>
                                                <td>
                                                    {u.email_count > 0 ? (
                                                        <button
                                                            className="rb-users-expand"
                                                            onClick={() => setExpandedEmailsUserId(
                                                                expandedEmailsUserId === u.id ? null : u.id
                                                            )}
                                                        >
                                                            {u.email_count} {expandedEmailsUserId === u.id ? '▲' : '▼'}
                                                        </button>
                                                    ) : '0'}
                                                </td>
                                                <td>
                                                    {u.email !== email && (
                                                        <button
                                                            className="rb-users-expand"
                                                            onClick={() => handleImpersonate(u.email)}
                                                            title={`Impersonate ${u.email}`}
                                                        >
                                                            Impersonate
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                            {expandedUserId === u.id && u.scheduled_classes.length > 0 && (
                                                <tr className="rb-users-detail-row">
                                                    <td colSpan={8}>
                                                        <ul className="rb-users-detail">
                                                            {u.scheduled_classes.map((c, i) => (
                                                                <li key={i}>
                                                                    📅 {c.date} ⏱ {c.hour}
                                                                    {c.class_name && (
                                                                        <span className="rb-users-classname"> · {c.class_name}</span>
                                                                    )}
                                                                    {c.is_monitoring && (
                                                                        <span className="rb-users-monitor"> 👀 monitoring</span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </td>
                                                </tr>
                                            )}
                                            {expandedEmailsUserId === u.id && u.recent_emails.length > 0 && (
                                                <tr className="rb-users-detail-row">
                                                    <td colSpan={8}>
                                                        <ul className="rb-users-detail">
                                                            {u.recent_emails.map((e, i) => (
                                                                <li key={i}>
                                                                    ✉️ {e.sent_at}
                                                                    <span className="rb-users-classname"> · {e.type}</span>
                                                                    {e.subject && (
                                                                        <span className="rb-users-monitor"> · {e.subject}</span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <div className="rb-modal-actions">
                            <button className="rb-btn-ghost" onClick={() => setUsersOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;
